import { Button } from '@/components/atoms';
import { CollectionGrid } from '../CollectionGrid';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '@/components/PageHeader';
import { useCreateCollectionGraphQL } from '@/hooks/graphql/useCollectionMutationsGraphQL';
import { useCollectionsGraphQL } from '@/graphql/hooks';
import { useCreateWithFiles } from '@/hooks/useCreateWithFiles';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useAuth } from '@/services/auth';
import { useNavigate } from 'react-router-dom';
import { collectionEditUrl } from '@/utils/urls';
import { FileDropZone } from '@/components/photos/FileDropZone';
import { AddExistingPhotosModal } from '@/components/composed';
import { CollectionIcon, FileIcon, FolderIcon, PhotoIcon } from '@/components/icons';
import { ACCEPTED_FILE_INPUT, filterValidFiles } from '@/utils/fileValidation';
import { generateCollectionName } from '@/utils/collectionName';
import { UPLOADS_BLOCKED_MESSAGE } from '@/types/upload';
import logger from '@/utils/logger';
import { useApolloClient } from '@apollo/client';
import { useUploadContext } from '@/contexts/UploadContext';

export interface CollectionListProps {
  className?: string;
}

export function CollectionList({ className = '' }: CollectionListProps) {
  const { collections, hasMore, loading, loadMore, error, refetch } = useCollectionsGraphQL();
  const { createCollection, loading: createLoading } = useCreateCollectionGraphQL();
  const { user } = useAuth();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { hasActiveUploads } = useUploadContext();

  // Infinite scroll
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    loading,
  });

  const handleCreateCollection = async (files?: File[]) => {
    if (!user) return;

    const defaultName = generateCollectionName(user.username);

    try {
      const newCollection = await createCollection({
        name: defaultName
      });

      // Refetch to update collections list
      apolloClient.refetchQueries({ include: ['GetCollections'] });

      // Navigate to the collection editor with files if provided
      if (newCollection?.globalId) {
        navigate(collectionEditUrl(newCollection.globalId, newCollection.name ? newCollection.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : ''), {
          state: { initialFiles: files }
        });
      }
    } catch (error) {
      logger.error('CollectionList', 'Failed to create collection:', error);
    }
  };

  // Handle creation with existing photos from library
  const handleCreateWithExistingPhotos = async (photoIds: string[]) => {
    if (!user || photoIds.length === 0) return;

    const defaultName = generateCollectionName(user.username);

    try {
      const newCollection = await createCollection({
        name: defaultName
      });

      // Refetch to update collections list
      apolloClient.refetchQueries({ include: ['GetCollections'] });

      // Navigate to the collection editor with photo IDs
      if (newCollection?.globalId) {
        navigate(collectionEditUrl(newCollection.globalId, newCollection.name ? newCollection.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : ''), {
          state: { initialPhotoIds: photoIds }
        });
      }
    } catch (error) {
      logger.error('CollectionList', 'Failed to create collection with existing photos:', error);
    }
  };

  // Use shared hook for file handling (with folder and existing photos support)
  const {
    fileInputRef,
    setFolderInputRef,
    menuRef,
    handleButtonClick,
    handleSelectFiles,
    handleSelectFolder,
    handleFilesDropped,
    handleInvalidFiles,
    isPending: isFilesPending,
    showUploadMenu,
    folderSupported,
    showFolderOption,
    showExistingPhotosOption,
    showExistingPhotosModal,
    handleSelectExistingPhotos,
    handleExistingPhotosSelected,
    handleCloseExistingPhotosModal
  } = useCreateWithFiles({
    onCreate: handleCreateCollection,
    onCreateWithExistingPhotos: handleCreateWithExistingPhotos,
    disabled: !user || createLoading || hasActiveUploads
  });

  // Combine pending states from both creation mutation and file handling
  const isPending = createLoading || isFilesPending;

  // Message to show when creation is blocked due to active uploads
  const uploadsBlockedReason = hasActiveUploads ? UPLOADS_BLOCKED_MESSAGE : undefined;

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Hidden file input for file picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_INPUT}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) handleCreateCollection(files);
        }}
        multiple
        className="hidden"
      />
      {/* Hidden folder input for folder picker */}
      {folderSupported && (
        <input
          ref={setFolderInputRef}
          type="file"
          accept={ACCEPTED_FILE_INPUT}
          onChange={(e) => {
            const rawFiles = Array.from(e.target.files || []);
            // Apply file validation for folder selection (may contain non-image files)
            const { valid: files, invalid } = filterValidFiles(rawFiles);
            if (invalid.length > 0) {
              logger.info('CollectionList', `Filtered out ${invalid.length} invalid files from folder selection`);
            }
            if (files.length > 0) handleCreateCollection(files);
          }}
          className="hidden"
        />
      )}

      <PageHeader>
        <PageHeaderLabel
          title="Collections"
          icon={<CollectionIcon size={32} />}
        />
        <PageHeaderCommandWell>
          <FileDropZone
            onFilesDropped={handleFilesDropped}
            onInvalidFiles={handleInvalidFiles}
            disabled={isPending || !user || hasActiveUploads}
            className="inline-block"
          >
            <div className="relative inline-block" title={uploadsBlockedReason}>
              <Button
                variant="primary"
                size="md"
                onClick={handleButtonClick}
                loading={isPending}
                disabled={isPending || !user || hasActiveUploads}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  {/* Folder base */}
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  {/* Upload arrow */}
                  <path d="M12 11v6" />
                  <path d="m9 14 3-3 3 3" />
                </svg>
                Create Collection
                {showFolderOption && folderSupported && (
                  <span className="ml-1">▾</span>
                )}
              </Button>

              {/* Dropdown menu for file/folder/existing photos selection */}
              {showUploadMenu && (
                <div
                  ref={menuRef}
                  className="absolute right-0 mt-1 w-48 bg-surface-1 border border-border rounded-md shadow-lg z-50"
                >
                  <button
                    type="button"
                    onClick={handleSelectFiles}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-t-md flex items-center gap-2"
                  >
                    <FileIcon size={16} />
                    Select Files
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectFolder}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 flex items-center gap-2"
                  >
                    <FolderIcon size={16} />
                    Select Folder
                  </button>
                  {showExistingPhotosOption && (
                    <button
                      type="button"
                      onClick={handleSelectExistingPhotos}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-surface-2 rounded-b-md flex items-center gap-2"
                    >
                      <PhotoIcon size={16} />
                      Existing Photos
                    </button>
                  )}
                </div>
              )}
            </div>
          </FileDropZone>
        </PageHeaderCommandWell>
      </PageHeader>

      <div className="flex-1 min-h-0 overflow-y-auto ">
        <CollectionGrid
          collections={collections}
          isLoading={loading && collections.length === 0}
          className='p-4'
          error={error ? new Error(error?.message || 'Failed to load collections') : null}
          onRetry={() => refetch()}
        />

        {/* Infinite scroll sentinel */}
        <div
          ref={sentinelRef}
          className="h-4"
          role="status"
          aria-live="polite"
          aria-label={loading && hasMore ? 'Loading more collections' : undefined}
        />
        {loading && hasMore && (
          <div className="flex justify-center py-4">
            <div className="text-neutral-400">Loading more collections...</div>
          </div>
        )}
      </div>

      {/* Existing photos selection modal */}
      <AddExistingPhotosModal
        open={showExistingPhotosModal}
        onClose={handleCloseExistingPhotosModal}
        onAdd={handleExistingPhotosSelected}
        existingPhotoIds={[]}
      />
    </div>
  );
}
